import React, { useState, useRef, useEffect } from 'react';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import apolloClient from '../../../../utils/apolloClient';
import styles from './PaymentInvoices.module.css';

const SEARCH_PLACES = gql`
  query SearchPlacesForInvoiceFilter($input: SearchInput) {
    search_places(input: $input) {
      place_listing {
        place_id
        name
        city
        state
      }
    }
  }
`;

const fetchPlaceSuggestions = async (userId, query) => {
  try {
    const { data } = await apolloClient.clientPrivate.query({
      query: SEARCH_PLACES,
      variables: {
        input: {
          user_id: userId,
          from: 0,
          size: 10,
          filter: { place_filter: { status: 'ACTIVE', place_name: query } },
        },
      },
      fetchPolicy: 'network-only',
    });
    return data?.search_places?.place_listing ?? [];
  } catch {
    return [];
  }
};

const SearchIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle cx="6.5" cy="6.5" r="5" stroke="#B8AAA2" strokeWidth="1.5" />
    <path
      d="M11 11L14.5 14.5"
      stroke="#B8AAA2"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const PlaceSearchWidget = ({ placeIdFilter, setPlaceIdFilter }) => {
  const { userId } = useStoreState(state => state.auth);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleQueryChange = e => {
    const { value } = e.target;
    setInputValue(value);
    if (placeIdFilter) setPlaceIdFilter('');
    clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSuggestions([]);
      setSearching(false);
      setDropdownOpen(false);
      return;
    }
    setSearching(true);
    setDropdownOpen(true);
    debounceRef.current = setTimeout(async () => {
      const results = await fetchPlaceSuggestions(userId, value);
      setSuggestions(results);
      setSearching(false);
    }, 300);
  };

  const handleSelect = place => {
    setPlaceIdFilter(place.place_id);
    setInputValue(place.name);
    setSuggestions([]);
    setDropdownOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setDropdownOpen(false);
    setPlaceIdFilter('');
    clearTimeout(debounceRef.current);
  };

  return (
    <div
      className={styles.filterGroup}
      style={{ flex: '1.5', minWidth: 200 }}
      ref={wrapperRef}
    >
      <div className={styles.formLabel}>Place</div>
      <div className={styles.placeSearchWrap}>
        <div className={styles.placeSearchBar}>
          <SearchIcon />
          <input
            className={styles.placeSearchInput}
            placeholder="Search place name…"
            value={inputValue}
            onChange={handleQueryChange}
            autoComplete="off"
          />
          {searching && <div className={styles.placeSearchSpinner} />}
          {!searching && inputValue && (
            <button className={styles.placeSearchClear} onClick={handleClear}>
              ✕
            </button>
          )}
        </div>
        {dropdownOpen && (
          <div className={styles.placeDropdown}>
            {searching && (
              <div
                className={styles.placeDropdownItem}
                style={{ color: '#B8AAA2', cursor: 'default' }}
              >
                Searching…
              </div>
            )}
            {!searching && suggestions.length === 0 && (
              <div
                className={styles.placeDropdownItem}
                style={{ color: '#B8AAA2', cursor: 'default' }}
              >
                No places found
              </div>
            )}
            {!searching &&
              suggestions.map(p => (
                <div
                  key={p.place_id}
                  className={styles.placeDropdownItem}
                  onMouseDown={() => handleSelect(p)}
                >
                  <span className={styles.placeDropdownName}>{p.name}</span>
                  <span className={styles.placeDropdownMeta}>
                    {p.city}, {p.state}
                  </span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

// NOTE: date-fns v2 (addDays, subDays, startOfMonth …) operates on local
// calendar days internally (setDate / setMonth). On DST spring-forward
// boundaries in UTC-offset timezones this can shift a result 1 hour away
// from UTC midnight, causing the wrong UTC calendar day to be used.
// All date arithmetic below therefore uses Date.UTC() + getUTC* methods,
// which are spec-guaranteed timezone-agnostic. If you need locale-aware
// formatting in the future, add date-fns-tz or upgrade to date-fns v3 (which
// has explicit UTC utilities).

const PERIOD_PRESETS = [
  { label: 'This Week', value: 'this_week' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Custom Range', value: 'custom' },
];

const STATUS_OPTIONS = [
  { label: 'All Status', value: '' },
  { label: 'Generated', value: 'GENERATED' },
  { label: 'Email Sent', value: 'SENT' },
  { label: 'Paid', value: 'PAID' },
  { label: 'Overdue', value: 'OVERDUE' },
  // { label: 'Draft', value: 'DRAFT' },
];

// ─── UTC helpers ─────────────────────────────────────────────────────────────
// Calendar dates are stored as UTC day boundaries.
// "2026-06-02" always means 2026-06-02T00:00:00.000Z regardless of the
// browser's local timezone.

/**
 * "2026-06-02" → "2026-06-02T00:00:00.000Z"
 *
 * Uses Date.UTC so a date-only string is never parsed as local midnight
 * (which would produce a different UTC date in any non-UTC timezone).
 */
const dateInputToUtcIso = value => {
  if (!value) return '';
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toISOString();
};

/**
 * "2026-06-02T00:00:00.000Z" → "2026-06-02"
 *
 * Uses getUTC* so the displayed day always matches the stored UTC calendar
 * day. getFullYear/getMonth/getDate would return the local calendar day,
 * which differs in any timezone offset from UTC.
 */
const toInputDate = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return [
    d.getUTCFullYear(),
    String(d.getUTCMonth() + 1).padStart(2, '0'),
    String(d.getUTCDate()).padStart(2, '0'),
  ].join('-');
};

/**
 * Returns "YYYY-MM-DDT00:00:00.000Z" for the day AFTER the UTC calendar day
 * of dateOrIso. This is the exclusive upper bound for any range whose last
 * included day is dateOrIso.
 *
 * Date.UTC handles month/year roll-over automatically
 * (e.g., month=11 day=32 → January of next year).
 *
 * Backend query contract:  created_at >= from  AND  created_at < to
 * No 23:59:59 timestamps are generated anywhere.
 */
const utcStartOfNextDay = dateOrIso => {
  const d = new Date(dateOrIso);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1),
  ).toISOString();
};

/**
 * UTC-safe day offset. n may be negative.
 *
 * Equivalent in intent to date-fns addDays/subDays, but implemented with
 * Date.UTC to avoid the local-time DST drift described at the top of this
 * file. Date.UTC propagates out-of-range day values correctly across month
 * and year boundaries.
 */
const utcOffsetDays = (dateOrIso, n) => {
  const d = new Date(dateOrIso);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n),
  ).toISOString();
};

// ─── Week anchor ──────────────────────────────────────────────────────────────

/**
 * Returns "YYYY-MM-DDT00:00:00.000Z" for the most recent Tuesday in UTC.
 * (Returns today if today is a Tuesday in UTC.)
 */
const thisWeekTuesdayUtc = () => {
  const now = new Date();
  const utcDow = now.getUTCDay(); // 0=Sun 1=Mon 2=Tue 3=Wed 4=Thu 5=Fri 6=Sat
  const daysSinceTue = (utcDow - 2 + 7) % 7;
  return new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate() - daysSinceTue,
    ),
  ).toISOString();
};

// ─── Month boundary helpers (billing cycle: first Tue → last Mon) ─────────────

/**
 * First Tuesday of the UTC month identified by (year, month).
 * month is 0-indexed. Date.UTC handles out-of-range values (e.g. month = -1).
 */
const firstTuesdayOfUtcMonth = (year, month) => {
  const firstDow = new Date(Date.UTC(year, month, 1)).getUTCDay();
  const daysToTue = (2 - firstDow + 7) % 7;
  return new Date(Date.UTC(year, month, 1 + daysToTue)).toISOString();
};

/**
 * Last Monday of the UTC month identified by (year, month).
 * Day 0 of month+1 = last calendar day of month; we walk back to Monday.
 */
const lastMondayOfUtcMonth = (year, month) => {
  const lastDate = new Date(Date.UTC(year, month + 1, 0));
  const lastDay = lastDate.getUTCDate();
  const lastDow = lastDate.getUTCDay();
  const daysToMon = (lastDow - 1 + 7) % 7;
  return new Date(Date.UTC(year, month, lastDay - daysToMon)).toISOString();
};

// ─── Date-picker snap helpers ─────────────────────────────────────────────────

/**
 * Snaps dateOrIso to the nearest Tuesday on or before it.
 * Used to constrain the "From" custom date picker.
 */
/**
 * Snaps dateOrIso to the nearest Monday on or before it.
 * Used to constrain the "To" custom date picker (the inclusive last day before
 * the exclusive upper bound is stored).
 */

// ─── Preset range factory ─────────────────────────────────────────────────────
// Billing cycle: Tuesday 00:00 UTC (inclusive) → next Tuesday 00:00 UTC (exclusive).
// "This week" on this dashboard = the last COMPLETED Tue–Mon cycle, not the
// current in-progress week. Accountants generate billing for closed periods.
// All ranges use exclusive upper bounds:  created_at >= from  AND  created_at < to

const getPresetRange = preset => {
  const now = new Date();

  switch (preset) {
    case 'this_week': {
      // Last completed billing week: previous Tuesday → current Tuesday (exclusive)
      const thisTue = thisWeekTuesdayUtc();
      const from = utcOffsetDays(thisTue, -7);
      return { from, to: thisTue };
    }

    case 'last_week': {
      // Week before last completed week
      const thisTue = thisWeekTuesdayUtc();
      const from = utcOffsetDays(thisTue, -14);
      const to = utcOffsetDays(thisTue, -7);
      return { from, to };
    }

    case 'this_month': {
      // Last completed calendar month: first Tuesday → day after last Monday
      const ref = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1),
      );
      const y = ref.getUTCFullYear();
      const m = ref.getUTCMonth();
      const from = firstTuesdayOfUtcMonth(y, m);
      const to = utcStartOfNextDay(lastMondayOfUtcMonth(y, m));
      return { from, to };
    }

    case 'last_month': {
      // Month before last completed month (two months ago)
      const ref = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 2, 1),
      );
      const y = ref.getUTCFullYear();
      const m = ref.getUTCMonth();
      const from = firstTuesdayOfUtcMonth(y, m);
      const to = utcStartOfNextDay(lastMondayOfUtcMonth(y, m));
      return { from, to };
    }

    case 'custom': {
      // Pre-fill with last completed week so pickers are never blank.
      const thisTue = thisWeekTuesdayUtc();
      const from = utcOffsetDays(thisTue, -7);
      return { from, to: thisTue };
    }

    default:
      return null;
  }
};

// ─── Component ────────────────────────────────────────────────────────────────

const InvoiceFilters = ({
  search,
  status,
  fromDate,
  toDate,
  period,
  placeIdFilter,
  setPlaceIdFilter,
  onSearchChange,
  onStatusChange,
  onFromChange: _onFromChange,
  onToChange: _onToChange,
  onRangeChange,
  onWeekPreset: _onWeekPreset,
  onPeriodChange,
  onApply,
  onExport,
}) => {
  const selectedPeriod = period ?? 'this_week';

  const handlePeriodChange = e => {
    const { value } = PERIOD_PRESETS[e.target.selectedIndex];
    onPeriodChange(value);
    const range = getPresetRange(value);
    // Single atomic call avoids the stale-closure problem that occurs when
    // onFromChange and onToChange are called in sequence — the second call
    // closes over an already-stale sibling date from the current render.
    onRangeChange(range.from, range.to);
  };

  const isCustom = selectedPeriod === 'custom';

  // toDate is stored as an exclusive upper bound (next-day 00:00 UTC).
  // Subtract 1 day when rendering so the picker shows the last included day.
  const toDisplayValue = toDate ? toInputDate(utcOffsetDays(toDate, -1)) : '';

  return (
    <div className={`${styles.card} ${styles.filterBar}`}>
      <div className={styles.filterRow}>
        <div className={`${styles.filterGroup} ${styles.filterGroupFlex}`}>
          <div className={styles.formLabel}>Search Invoice </div>
          <input
            className={styles.formInput}
            placeholder="🔍  Invoice no..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
          />
        </div>

        <PlaceSearchWidget
          placeIdFilter={placeIdFilter}
          setPlaceIdFilter={setPlaceIdFilter}
        />

        {/* <div className={styles.filterGroup}>
          <div className={styles.formLabel}>Status</div>
          <select
            className={styles.formSelect}
            value={status}
            onChange={e => onStatusChange(e.target.value)}
          >
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div> */}

        <div className={styles.filterGroup}>
          <div className={styles.formLabel}>Period</div>
          <select
            className={styles.formSelect}
            value={selectedPeriod}
            onChange={handlePeriodChange}
          >
            {PERIOD_PRESETS.map(p => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <div className={styles.formLabel}>From</div>
          <input
            type="date"
            className={`${styles.formInput} ${styles.formInputDate}`}
            value={toInputDate(fromDate)}
            disabled={!isCustom}
            min="1970-01-06"
            max={toInputDate(new Date().toISOString())}
            step="1"
            onChange={e => {
              if (!e.target.value) return;
              const selected = dateInputToUtcIso(e.target.value);
              onRangeChange(selected, utcOffsetDays(selected, 7));
            }}
          />
        </div>

        <div className={styles.filterGroup}>
          <div className={styles.formLabel}>To</div>
          <input
            type="date"
            className={`${styles.formInput} ${styles.formInputDate}`}
            value={toDisplayValue}
            disabled={!isCustom || !fromDate}
            min={toInputDate(fromDate)}
            max={toInputDate(new Date().toISOString())}
            onChange={e => {
              if (!e.target.value) return;
              onRangeChange(
                fromDate,
                utcStartOfNextDay(dateInputToUtcIso(e.target.value)),
              );
            }}
          />
        </div>

        <button
          style={{ marginBottom: '0.4rem' }}
          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
          onClick={onApply}
        >
          Apply
        </button>
        {/* <button
          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
          onClick={onExport}
        >
          ⬇ Export
        </button> */}
      </div>
    </div>
  );
};

export default InvoiceFilters;
